import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  Star,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  Users,
  ThumbsUp,
  ChevronRight,
  Quote,
  ArrowRight,
  Sparkles,
  BadgeCheck,
} from 'lucide-react';
import { SearchBar } from '@/components/search/SearchBar';
import { MapSearchSection } from '@/components/search/MapSearchSection';

const stats = [
  { value: '10,000+', label: 'verifiedPros', icon: BadgeCheck, color: 'text-green-600 bg-green-100' },
  { value: '500,000+', label: 'completedJobs', icon: CheckCircle, color: 'text-blue-600 bg-blue-100' },
  { value: '4.8', label: 'averageRating', icon: Star, color: 'text-yellow-600 bg-yellow-100' },
  { value: '24/7', label: 'availability', icon: Clock, color: 'text-purple-600 bg-purple-100' },
];

const testimonials = [
  {
    nameKey: 'testimonials.customer1.name',
    locationKey: 'testimonials.customer1.location',
    rating: 5,
    textKey: 'testimonials.customer1.text',
    serviceKey: 'testimonials.customer1.service',
    gradient: 'from-blue-500 to-blue-700',
  },
  {
    nameKey: 'testimonials.customer2.name',
    locationKey: 'testimonials.customer2.location',
    rating: 5,
    textKey: 'testimonials.customer2.text',
    serviceKey: 'testimonials.customer2.service',
    gradient: 'from-green-500 to-green-700',
  },
  {
    nameKey: 'testimonials.customer3.name',
    locationKey: 'testimonials.customer3.location',
    rating: 5,
    textKey: 'testimonials.customer3.text',
    serviceKey: 'testimonials.customer3.service',
    gradient: 'from-orange-500 to-orange-700',
  },
];

const faqs = [
  { question: 'faq1Question', answer: 'faq1Answer' },
  { question: 'faq2Question', answer: 'faq2Answer' },
  { question: 'faq3Question', answer: 'faq3Answer' },
  { question: 'faq4Question', answer: 'faq4Answer' },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};

export function Home() {
  const { t } = useTranslation(['home', 'common']);

  return (
    <div className="overflow-hidden">
      {/* Hero Section - Simplified */}
      <section className="relative pt-8 pb-12 lg:pt-16 lg:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary-50 via-white to-white overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Shield className="w-4 h-4" />
              {t('hero.trustBadge')}
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
            >
              {t('hero.title').split(' ').slice(0, -2).join(' ')}{' '}
              <span className="text-primary-600">{t('hero.title').split(' ').slice(-2).join(' ')}</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="mt-6 text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Search Bar */}
            <motion.div variants={fadeInUp} className="mt-8 max-w-2xl mx-auto">
              <SearchBar variant="hero" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Map Search Section - Full width with categories on map */}
      <section className="bg-gray-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MapSearchSection
            title={t('map.title')}
            subtitle={t('map.subtitle')}
            height="70vh"
            showViewAll
            showCategoryFilters
          />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="text-center"
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 ${stat.color} rounded-2xl mb-4`}>
                  <stat.icon className="w-7 h-7" />
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500 font-medium">{t(`stats.${stat.label}`)}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
            >
              {t('whyChoose.title')}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              {t('whyChoose.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-200"
            >
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {t('whyChoose.verified.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('whyChoose.verified.description')}
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-200"
            >
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {t('whyChoose.reviews.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('whyChoose.reviews.description')}
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <ThumbsUp className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {t('whyChoose.satisfaction.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('whyChoose.satisfaction.description')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works - Vertical Timeline */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16 lg:mb-20"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-4"
            >
              <CheckCircle className="w-4 h-4" />
              {t('howItWorks.badge', 'Simple 3-Step Process')}
            </motion.div>
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4"
            >
              {t('howItWorks.title')}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              {t('howItWorks.subtitle')}
            </motion.p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line - desktop */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2">
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: '100%' }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className="w-full bg-gradient-to-b from-primary-300 via-primary-500 to-primary-300"
              />
            </div>

            {/* Vertical line - mobile */}
            <div className="lg:hidden absolute left-8 top-0 bottom-0 w-0.5">
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: '100%' }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className="w-full bg-gradient-to-b from-primary-300 via-primary-500 to-primary-300"
              />
            </div>

            {/* Steps */}
            <div className="space-y-12 lg:space-y-0">
              {[
                {
                  icon: MapPin,
                  step: 1,
                  titleKey: 'howItWorks.step1.title',
                  descKey: 'howItWorks.step1.description',
                  color: 'from-blue-500 to-blue-600',
                  bgColor: 'bg-blue-50',
                  iconBg: 'bg-blue-100',
                  features: ['howItWorks.step1.feature1', 'howItWorks.step1.feature2'],
                },
                {
                  icon: Users,
                  step: 2,
                  titleKey: 'howItWorks.step2.title',
                  descKey: 'howItWorks.step2.description',
                  color: 'from-purple-500 to-purple-600',
                  bgColor: 'bg-purple-50',
                  iconBg: 'bg-purple-100',
                  features: ['howItWorks.step2.feature1', 'howItWorks.step2.feature2'],
                },
                {
                  icon: Phone,
                  step: 3,
                  titleKey: 'howItWorks.step3.title',
                  descKey: 'howItWorks.step3.description',
                  color: 'from-green-500 to-green-600',
                  bgColor: 'bg-green-50',
                  iconBg: 'bg-green-100',
                  features: ['howItWorks.step3.feature1', 'howItWorks.step3.feature2'],
                },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className={`relative lg:grid lg:grid-cols-2 lg:gap-12 items-center ${
                    index % 2 === 1 ? 'lg:direction-rtl' : ''
                  }`}
                >
                  {/* Timeline node - mobile */}
                  <div className="lg:hidden absolute left-8 top-0 -translate-x-1/2 z-10">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.2, type: 'spring', stiffness: 200 }}
                      className={`w-16 h-16 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}
                    >
                      <item.icon className="w-7 h-7 text-white" />
                    </motion.div>
                  </div>

                  {/* Content - Left side on desktop (or right for odd items) */}
                  <div className={`pl-24 lg:pl-0 ${index % 2 === 1 ? 'lg:col-start-2 lg:pl-12' : 'lg:pr-12 lg:text-right'}`}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                      className={`${item.bgColor} rounded-2xl p-6 lg:p-8 relative overflow-hidden`}
                    >
                      {/* Step number badge */}
                      <div className={`inline-flex items-center gap-2 bg-white rounded-full px-3 py-1 text-sm font-bold mb-4 shadow-sm`}>
                        <span className={`w-6 h-6 rounded-full bg-gradient-to-br ${item.color} text-white flex items-center justify-center text-xs`}>
                          {item.step}
                        </span>
                        <span className="text-gray-700">Step {item.step}</span>
                      </div>

                      <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                        {t(item.titleKey)}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {t(item.descKey)}
                      </p>

                      {/* Feature bullets */}
                      <ul className={`space-y-2 ${index % 2 === 1 ? '' : 'lg:flex lg:flex-col lg:items-end'}`}>
                        {item.features.map((feature, fIndex) => (
                          <motion.li
                            key={fIndex}
                            initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.5 + fIndex * 0.1 }}
                            className="flex items-center gap-2 text-sm text-gray-700"
                          >
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{t(feature)}</span>
                          </motion.li>
                        ))}
                      </ul>

                      {/* Decorative element */}
                      <div className={`absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-gradient-to-br ${item.color} opacity-10`} />
                    </motion.div>
                  </div>

                  {/* Timeline node - desktop */}
                  <div className={`hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10`}>
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.2, type: 'spring', stiffness: 200 }}
                      whileHover={{ scale: 1.1 }}
                      className={`w-20 h-20 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center shadow-xl cursor-pointer`}
                    >
                      <item.icon className="w-9 h-9 text-white" />
                    </motion.div>
                  </div>

                  {/* Empty space for alternating layout */}
                  <div className={`hidden lg:block ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16 lg:mt-20"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/search"
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-xl shadow-primary-600/25"
              >
                {t('howItWorks.cta')}
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </Link>
            </motion.div>
            <p className="mt-4 text-sm text-gray-500">{t('howItWorks.ctaSubtext', 'Free to use. No account required.')}</p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
            >
              {t('testimonials.title')}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              {t('testimonials.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => {
              const name = t(testimonial.nameKey);
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 relative"
                >
                  <Quote className="w-12 h-12 text-primary-100 absolute top-6 right-6" />
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed relative z-10">
                    "{t(testimonial.textKey)}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br ${testimonial.gradient}`}
                    >
                      <span className="text-xl font-bold text-white">
                        {name[0]}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{name}</div>
                      <div className="text-sm text-gray-500">{t(testimonial.locationKey)}</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-xs text-primary-600 font-medium bg-primary-50 px-3 py-1.5 rounded-full">
                      {t(testimonial.serviceKey)}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
            >
              {t('faq.title')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600">
              {t('faq.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.details
                key={index}
                variants={fadeInUp}
                className="group bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden hover:border-primary-200 transition-colors"
              >
                <summary className="flex items-center justify-between cursor-pointer p-6 text-left font-semibold text-gray-900 hover:text-primary-600 transition-colors">
                  {t(`faq.${faq.question}`)}
                  <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform duration-200" />
                </summary>
                <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                  {t(`faq.${faq.answer}`)}
                </div>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      {/* For Pros CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 rounded-3xl p-8 md:p-12 lg:p-16 text-white overflow-hidden"
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />
            <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-primary-400/10 rounded-full blur-2xl" />

            <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('forPros.badge')}</span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-4"
                >
                  {t('forPros.title')}
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="text-primary-100 mb-8 text-lg leading-relaxed"
                >
                  {t('forPros.description')}
                </motion.p>

                <motion.ul
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  {['benefit1', 'benefit2', 'benefit3'].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      </div>
                      <span className="text-white/90">{t(`forPros.${benefit}`)}</span>
                    </li>
                  ))}
                </motion.ul>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-col gap-4 lg:pl-8"
              >
                <a
                  href="https://hvacfrontdesk.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white text-primary-600 hover:bg-gray-50 text-center py-4 px-8 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 transition-all hover:scale-105 shadow-lg"
                >
                  {t('forPros.frontdeskCta')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <p className="text-center text-sm text-primary-200">
                  {t('forPros.noCreditCard')}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-4xl font-bold text-white mb-4"
            >
              {t('finalCta.title')}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto"
            >
              {t('finalCta.subtitle')}
            </motion.p>
            <motion.div variants={fadeInUp} className="max-w-2xl mx-auto">
              <SearchBar variant="default" />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
